import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Chip,
  Tabs,
  Tab,
  Card,
  CardBody,
  Pagination,
} from "@nextui-org/react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faRefresh,
  faSort,
  faSortUp,
  faSortDown,
  faGripVertical,
  faList,
  faArrowUp,
  faArrowDown,
  faPlus,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import {
  getDataWithFilterUrl,
  StringLimit,
  capitalize,
} from "../../controllers/APIController";
import { SearchInput } from "../../utils/helper";
import LoadingPage from "../../components/Loader/LoadingPage";
import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import NullData from "../../components/ErrorPage/NullData";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const PlaceholderPage = () => {
  // All state declarations at the top level
  const [criticalError, setCriticalError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [layoutMode, setLayoutMode] = useState("cards");
  const [pageData, setPageData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [groupedData, setGroupedData] = useState({});
  const [error, setError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [renderError, setRenderError] = useState(null);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const [isViewSwitching, setIsViewSwitching] = useState(false);

  const navigate = useNavigate();
  const { _orgId } = useParams();
  const location = useLocation();
  const [url, setUrl] = useState("");
  const UserLogged = JSON.parse(sessionStorage.getItem("userData"));
  const OrgInfo = UserLogged && UserLogged.user.LastOrgId;
  const orgFilterUrl = "filters[Organization][id][$eqi]=" + OrgInfo;

  const {
    data: metricData,
    isPending,
    refetch,
  } = useQuery({
    queryKey: ["metrics", url],
    queryFn: () => getDataWithFilterUrl("metrics", url),
    keepPreviousData: true,
  });

  console.log("->>", metricData);

  // 100 call
  // paginationCount
  // if selection + length>100*current

  

  // Safe string conversion utility function
  const safeString = useCallback((value, defaultValue = "") => {
    try {
      if (value === null || value === undefined) {
        return defaultValue;
      }

      if (typeof value === "symbol") {
        return "[Symbol]";
      }

      if (typeof value === "string") {
        return value;
      }

      if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
      }

      if (typeof value === "object") {
        try {
          return JSON.stringify(value, (key, val) => {
            if (typeof val === "symbol") {
              return "[Symbol]";
            }
            return val;
          });
        } catch (err) {
          return defaultValue;
        }
      }

      return String(value);
    } catch (err) {
      return defaultValue;
    }
  }, []);

  // Error handlers moved to useCallback to prevent recreation
  const handleCriticalError = useCallback((error, context = "") => {
    console.error(
      `Critical error in PlaceholderPage${context ? ` (${context})` : ""}:`,
      error
    );
    setCriticalError(error);
  }, []);

  const handleRenderError = useCallback((error, context = "") => {
    console.error(
      `Render error in PlaceholderPage${context ? ` (${context})` : ""}:`,
      error
    );

    if (error.message && error.message.includes("Symbol")) {
      setRenderError(
        new Error("Data contains invalid values. Refreshing automatically...")
      );
      setIsAutoRefreshing(true);

      const timeoutId = setTimeout(() => {
        setRenderError(null);
        setIsAutoRefreshing(false);
        handleRefresh();
      }, 2000);

      return () => clearTimeout(timeoutId);
    }

    setRenderError(error);
  }, []);

  // Global error handler for the entire component
  useEffect(() => {
    const handleSymbolError = (event) => {
      const error = event.error || event.reason;

      if (error && error.message && error.message.includes("Symbol")) {
        console.error("Symbol error detected in PlaceholderPage:", error);

        event.preventDefault();
        event.stopPropagation();

        setCriticalError(
          new Error("Symbol value error detected. Please refresh the page.")
        );

        console.warn("Symbol error context:", {
          message: error.message,
          stack: error.stack,
          timestamp: new Date().toISOString(),
        });
      }
    };

    window.addEventListener("error", handleSymbolError);
    window.addEventListener("unhandledrejection", handleSymbolError);

    return () => {
      window.removeEventListener("error", handleSymbolError);
      window.removeEventListener("unhandledrejection", handleSymbolError);
    };
  }, []);

  // Global error handler for unhandled errors
  useEffect(() => {
    const handleError = (error) => {
      console.error("Unhandled error in PlaceholderPage:", error);
      handleRenderError(error, "unhandled");
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", (event) =>
      handleError(event.reason)
    );

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", (event) =>
        handleError(event.reason)
      );
    };
  }, [handleRenderError]);

  // Cleanup effect to clear render errors when data is successfully loaded
  useEffect(() => {
    if (renderError && (pageData.length > 0 || filteredData.length > 0)) {
      console.log("Data loaded successfully, clearing render error");
      setRenderError(null);
      setIsAutoRefreshing(false);
    }
  }, [pageData.length, filteredData.length, renderError]);

  const getPageConfig = () => {
    const path = location.pathname;
    const pathSegments = path.split("/").filter(Boolean);
    const mainCategory = pathSegments[1];
    const subCategory = pathSegments[2];

    const pageConfigs = {
      "ghg-emissions": {
        "scope-1": {
          title: "Scope 1 Emissions",
          description: "Direct emissions from owned or controlled sources",
          filterKey: "Scope 1",
        },
        "scope-2": {
          title: "Scope 2 Emissions",
          description: "Indirect emissions from purchased energy",
          filterKey: "Scope 2",
        },
        "scope-3": {
          title: "Scope 3 Emissions",
          description:
            "Indirect emissions from the value chain including upstream and downstream activities",
          filterKey: "Scope 3",
        },
        "fugitive-emissions": {
          title: "Fugitive Emissions",
          description:
            "Fugitive emissions from equipment leaks and other sources",
          filterKey: "Fugitive Emissions",
        },
      },
      energy: {
        default: {
          title: "Energy",
          description: "Energy consumption and efficiency metrics",
          filterKey: "Energy",
        },
        renewable: {
          title: "Renewable Energy",
          description: "Renewable energy consumption and generation",
          filterKey: "Renewable Energy",
        },
        consumption: {
          title: "Energy Consumption",
          description: "Total energy consumption and usage patterns",
          filterKey: "Energy Consumption",
        },
        efficiency: {
          title: "Energy Efficiency",
          description: "Energy efficiency improvements and optimization",
          filterKey: "Energy Efficiency",
        },
      },
      water: {
        default: {
          title: "Water",
          description: "Water consumption and management metrics",
          filterKey: "Water",
        },
        consumption: {
          title: "Water Consumption",
          description: "Water consumption and usage patterns",
          filterKey: "Water Consumption",
        },
        quality: {
          title: "Water Quality",
          description: "Water quality monitoring and management",
          filterKey: "Water Quality",
        },
        conservation: {
          title: "Water Conservation",
          description: "Water conservation and efficiency initiatives",
          filterKey: "Water Conservation",
        },
      },
      "waste-circularity": {
        default: {
          title: "Waste & Circularity",
          description: "Waste management and circular economy metrics",
          filterKey: "Waste Circularity",
        },
        generation: {
          title: "Waste Generation",
          description: "Waste generation and classification",
          filterKey: "Waste Generation",
        },
        management: {
          title: "Waste Management",
          description: "Waste management and disposal practices",
          filterKey: "Waste Management",
        },
        economy: {
          title: "Circular Economy",
          description: "Circular economy initiatives and practices",
          filterKey: "Circular Economy",
        },
      },
      "health-safety": {
        default: {
          title: "Health & Safety",
          description: "Health and safety performance metrics",
          filterKey: "Health Safety",
        },
        incidents: {
          title: "Safety Incidents",
          description: "Safety incident tracking and analysis",
          filterKey: "Incident Reporting",
        },
        training: {
          title: "Safety Training",
          description: "Safety training and education programs",
          filterKey: "Safety Training",
        },
        risk: {
          title: "Risk Assessment",
          description: "Health and safety risk assessments",
          filterKey: "Risk Management",
        },
      },
      "human-rights-labor": {
        default: {
          title: "Human Rights & Labor",
          description: "Human rights and labor standards metrics",
          filterKey: "Human Rights Labor",
        },
        standards: {
          title: "Labor Standards",
          description: "Labor standards and compliance metrics",
          filterKey: "Labor Standards",
        },
        rights: {
          title: "Human Rights",
          description: "Human rights policies and practices",
          filterKey: "Human Rights",
        },
        diversity: {
          title: "Diversity & Inclusion",
          description: "Diversity and inclusion initiatives",
          filterKey: "Diversity Inclusion",
        },
      },
      assessments: {
        default: {
          title: "Assessments",
          description: "ESG assessment and evaluation metrics",
          filterKey: "Assessments",
        },
        internal: {
          title: "Internal Assessments",
          description: "Internal assessment and evaluation processes",
          filterKey: "Internal Assessments",
        },
        stakeholder: {
          title: "Stakeholder Surveys",
          description: "Stakeholder feedback and survey results",
          filterKey: "Stakeholder Surveys",
        },
        "third-party": {
          title: "Third-Party Audits",
          description: "External audit and certification processes",
          filterKey: "Third Party Audits",
        },
      },

      "reference-data": {
        default: {
          title: "Reference Data",
          description: "Reference data and lookup tables",
          filterKey: "Reference Data",
          isReferenceData: true,
        },
        environmental: {
          title: "Environmental Reference Data",
          description: "Environmental classifications and reference data",
          filterKey: "Environmental",
          isReferenceData: true,
        },
        "financial-economics": {
          title: "Financial and Economics Reference Data",
          description: "Financial and economic reference classifications",
          filterKey: "Financial and Economics",
          isReferenceData: true,
        },
        "corporate-governance": {
          title: "Corporate Governance Reference Data",
          description: "Corporate governance reference classifications",
          filterKey: "Corporate Governance",
          isReferenceData: true,
        },
        "social-people": {
          title: "Social and People Reference Data",
          description: "Social and people-related reference classifications",
          filterKey: "Social and People",
          isReferenceData: true,
        },
        units: {
          title: "Organizational Units",
          description: "Organizational unit reference data",
          filterKey: "Units",
          isReferenceData: true,
        },
      },
    };

    const config = pageConfigs[mainCategory];

    if (!config) {
      console.log(
        "🔍 getPageConfig: No config found for main category:",
        mainCategory
      );
      return {
        title: "Metrics",
        description: "Performance metrics and data",
        filterKey: mainCategory,
      };
    }

    const subConfig = config[subCategory] || config.default;

    if (!subConfig) {
      console.log(
        "🔍 getPageConfig: No sub config found for:",
        subCategory || mainCategory
      );
      return {
        title: "Metrics",
        description: "Performance metrics and data",
        filterKey: subCategory || mainCategory,
      };
    }

    return subConfig;
  };

  const pageConfig = getPageConfig();

  const handleSearch = (value) => {
    setSearchTerm(value);
    if (!value.trim()) {
      setFilteredData(pageData);
    } else {
      const filtered = pageData.filter((item) => {
        try {
          // Safety check: ensure item is valid
          if (!item || typeof item === "symbol") return false;
          if (!item.attributes || typeof item.attributes !== "object")
            return false;

          // Don't filter out items with Symbol values - just process them safely
          const name = item?.attributes?.Name;
          const description = item?.attributes?.Description;

          return (
            (name && name.toLowerCase().includes(value.toLowerCase())) ||
            (description &&
              description.toLowerCase().includes(value.toLowerCase()))
          );
        } catch (err) {
          console.warn("Error in search filter:", err);
          return false;
        }
      });
      setFilteredData(filtered);
    }
    setCurrentPage(1);
  };

  const handleCategorySelection = (key) => {
    if (key === "") {
      setSelectedCategory("");
      setFilteredData(pageData);
    } else {
      setSelectedCategory(key);
      // For Scope 3, filter by ClassName instead of Category
      const filtered = pageData.filter((item) => {
        try {
          // Safety check: ensure item is valid
          if (!item || typeof item === "symbol") return false;
          if (!item.attributes || typeof item.attributes !== "object")
            return false;

          // Don't filter out items with Symbol values - just process them safely
          if (pageConfig.filterKey === "Scope 3") {
            const className = item?.attributes?.ClassName || "";
            if (key === "Upstream") return className.includes("Upstream");
            if (key === "Downstream") return className.includes("Downstream");
            return false;
          } else {
            return item?.attributes?.Category === key;
          }
        } catch (err) {
          console.warn("Error in category filter:", err);
          return false;
        }
      });
      setFilteredData(filtered);
    }
    setCurrentPage(1);
  };

  useEffect(() => {
    handleRefresh();
  }, [metricData]);

  const handleRefresh = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const pageConfig = getPageConfig();
      let filterUrl = orgFilterUrl;

      if (pageConfig.filterKey === "Scope 1") {
        filterUrl += `&filters[ClassName][$eq]=Scope 1: Direct Emissions`;
      } else if (pageConfig.filterKey === "Scope 2") {
        filterUrl += `&filters[ClassName][$eq]=Scope 2: Indirect emissions`;
      } else if (pageConfig.filterKey === "Scope 3") {
        filterUrl += `&filters[ClassName][$contains]=Scope 3`;
      } else if (pageConfig.filterKey === "Energy") {
        filterUrl += `&filters[ClassName][$eq]=Energy Consumption`;
      } else if (pageConfig.filterKey === "Water") {
        filterUrl += `&filters[ClassName][$contains]=Water`;
      } else if (pageConfig.filterKey === "Water Consumption") {
        filterUrl += `&filters[ClassName][$eq]=Water Consumption`;
      } else if (pageConfig.filterKey === "Water Quality") {
        filterUrl += `&filters[ClassName][$eq]=Water Quality`;
      } else if (pageConfig.filterKey === "Water Conservation") {
        filterUrl += `&filters[ClassName][$eq]=Water Conservation`;
      } else if (pageConfig.filterKey === "Waste Circularity") {
        filterUrl += `&filters[ClassName][$contains]=Waste`;
      } else if (pageConfig.filterKey === "Health Safety") {
        filterUrl += `&filters[ClassName][$contains]=Safety`;
      } else if (pageConfig.filterKey === "Human Rights Labor") {
        filterUrl += `&filters[ClassName][$contains]=Human Rights`;
      } else if (pageConfig.filterKey === "Assessments") {
        filterUrl += `&filters[ClassName][$contains]=Assessment`;
      } else if (pageConfig.filterKey === "Stakeholder Surveys") {
        filterUrl += `&filters[ClassName][$contains]=Stakeholder`;
      } else if (pageConfig.filterKey === "Third Party Audits") {
        filterUrl += `&filters[ClassName][$contains]=Third Party`;
      } else if (pageConfig.filterKey === "Task Tracker") {
        filterUrl += `&filters[ClassName][$contains]=Task Tracker`;
      } else if (pageConfig.filterKey === "GHG Emissions Task Tracker") {
        filterUrl += `&filters[ClassName][$contains]=GHG Emissions Task Tracker`;
      } else if (pageConfig.filterKey === "Energy Task Tracker") {
        filterUrl += `&filters[ClassName][$contains]=Energy Task Tracker`;
      } else if (pageConfig.filterKey === "Water Task Tracker") {
        filterUrl += `&filters[ClassName][$contains]=Water Task Tracker`;
      } else if (pageConfig.filterKey === "Waste Circularity Task Tracker") {
        filterUrl += `&filters[ClassName][$contains]=Waste Circularity Task Tracker`;
      } else if (pageConfig.filterKey === "Health Safety Task Tracker") {
        filterUrl += `&filters[ClassName][$contains]=Health Safety Task Tracker`;
      } else if (pageConfig.filterKey === "Human Rights Labor Task Tracker") {
        filterUrl += `&filters[ClassName][$contains]=Human Rights Labor Task Tracker`;
      } else if (
        pageConfig.filterKey === "Organisational Governance Task Tracker"
      ) {
        filterUrl += `&filters[ClassName][$contains]=Organisational Governance Task Tracker`;
      } else if (pageConfig.filterKey === "Assessments Task Tracker") {
        filterUrl += `&filters[ClassName][$contains]=Assessments Task Tracker`;
      } else if (pageConfig.filterKey === "Reference Data") {
        filterUrl += `&filters[Category][$eqi]=Reference Data`;
      } else if (
        pageConfig.filterKey === "Environmental" ||
        pageConfig.filterKey === "Financial and Economics" ||
        pageConfig.filterKey === "Corporate Governance" ||
        pageConfig.filterKey === "Social and People" ||
        pageConfig.filterKey === "Units"
      ) {
        // For reference data subsections, filter by Category = "Reference Data" and handle filtering in the component
        filterUrl += `&filters[Category][$eqi]=Reference Data`;
      } else {
        filterUrl += `&filters[ClassName][$contains]=${pageConfig.filterKey}`;
      }
      setUrl(filterUrl);
      const responseData = metricData?.data || [];
      console.log("raw metricData",metricData)

      let validData = responseData.filter(
        (item) =>
          item &&
          item.id &&
          item.attributes &&
          (typeof item.id === "string" || typeof item.id === "number")
      );
       
      console.log("filter->validData",validData)
      // Additional validation to check for Symbol values before processing
      validData = validData.filter((item) => {
        try {
          // Check if the item contains any Symbol values
          const hasSymbols = JSON.stringify(item, (key, val) => {
            if (typeof val === "symbol") {
              console.warn("Symbol value found during validation:", key, val);
              return null; // Mark for removal
            }
            return val;
          });
          return hasSymbols !== null;
        } catch (err) {
          console.warn("Error validating item for Symbol values:", err, item);
          return false; // Remove problematic items
        }
      });

        console.log("filter->2 validData", validData);
      // Enhanced data sanitization to handle Symbol values and other problematic data types
      validData = validData.map((item) => {
        const sanitizedItem = { ...item };
        if (sanitizedItem.attributes) {
          // Deep clone and sanitize attributes
          const sanitizedAttributes = {};
          Object.keys(sanitizedItem.attributes).forEach((key) => {
            const value = sanitizedItem.attributes[key];

            // Handle Symbol values
            if (typeof value === "symbol") {
              console.warn("Symbol value found in attributes:", key, value);
              sanitizedAttributes[key] = "[Symbol]";
            }
            // Handle nested objects
            else if (
              value &&
              typeof value === "object" &&
              !Array.isArray(value)
            ) {
              try {
                const sanitizedNested = {};
                Object.keys(value).forEach((nestedKey) => {
                  const nestedValue = value[nestedKey];
                  if (typeof nestedValue === "symbol") {
                    console.warn(
                      "Symbol value found in nested object:",
                      nestedKey,
                      nestedValue
                    );
                    sanitizedNested[nestedKey] = "[Symbol]";
                  } else {
                    sanitizedNested[nestedKey] = nestedValue;
                  }
                });
                sanitizedAttributes[key] = sanitizedNested;
              } catch (err) {
                console.warn("Error sanitizing nested object:", err);
                sanitizedAttributes[key] = safeString(value, "--");
              }
            }
            // Handle arrays
            else if (Array.isArray(value)) {
              try {
                sanitizedAttributes[key] = value.map((arrayItem) => {
                  if (typeof arrayItem === "symbol") {
                    console.warn("Symbol value found in array:", arrayItem);
                    return "[Symbol]";
                  } else if (arrayItem && typeof arrayItem === "object") {
                    // Recursively sanitize array objects
                    const sanitizedArrayItem = {};
                    Object.keys(arrayItem).forEach((arrayKey) => {
                      const arrayValue = arrayItem[arrayKey];
                      if (typeof arrayValue === "symbol") {
                        console.warn(
                          "Symbol value found in array object:",
                          arrayKey,
                          arrayValue
                        );
                        sanitizedArrayItem[arrayKey] = "[Symbol]";
                      } else {
                        sanitizedArrayItem[arrayKey] = arrayValue;
                      }
                    });
                    return sanitizedArrayItem;
                  }
                  return arrayItem;
                });
              } catch (err) {
                console.warn("Error sanitizing array:", err);
                sanitizedAttributes[key] = [safeString(value, "--")];
              }
            }
            // Handle other types
            else {
              sanitizedAttributes[key] = value;
            }
          });
          sanitizedItem.attributes = sanitizedAttributes;
        }
        return sanitizedItem;
      });

      console.log("filter 3 vd",validData)

      // Use the data directly since we're no longer using tables
      const finalCleanData = validData;

      // For reference data subsections, filter by ClassName containing the subsection name
      if (
        pageConfig.filterKey === "Environmental" ||
        pageConfig.filterKey === "Financial and Economics" ||
        pageConfig.filterKey === "Corporate Governance" ||
        pageConfig.filterKey === "Social and People" ||
        pageConfig.filterKey === "Units"
      ) {
        console.log(
          `Before filtering, all ClassNames:`,
          finalCleanData.map((item) => item?.attributes?.ClassName)
        );
        const filteredData = finalCleanData.filter((item) => {
          const className = item?.attributes?.ClassName || "";
          return className
            .toLowerCase()
            .includes(pageConfig.filterKey.toLowerCase());
        });

        validData = filteredData;
      } else {
        validData = finalCleanData;
      }

      validData.sort((a, b) => {
        const nameA = a?.attributes?.Name || "";
        const nameB = b?.attributes?.Name || "";
        return nameA.localeCompare(nameB);
      });

      setPageData(validData);
      setFilteredData(validData);

      // Group data by ClassName with additional Symbol value protection
      const grouped = {};
      validData.forEach((item) => {
        try {
          // Ensure className is safely converted to string
          const className = safeString(
            item?.attributes?.ClassName,
            "Uncategorized"
          );
          if (!grouped[className]) {
            grouped[className] = [];
          }
          grouped[className].push(item);
        } catch (err) {
          console.warn("Error processing item for grouping:", err, item);
          // Fallback to uncategorized
          const fallbackKey = "Uncategorized";
          if (!grouped[fallbackKey]) {
            grouped[fallbackKey] = [];
          }
          grouped[fallbackKey].push(item);
        }
      });
      setGroupedData(grouped);
    } catch (err) {
      console.error("Error fetching data:", err);

      // Check if this is a Symbol-related error
      if (err.message && err.message.includes("Symbol")) {
        console.log("Symbol error in data fetching, attempting recovery");
        handleRenderError(err, "data fetching");
        return;
      }

      setError("Failed to fetch data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Functions for reference data actions
  const handleEditReferenceData = (item) => {
    console.log("Edit reference data:", item);
    // TODO: Implement edit functionality
  };

  const handleDeleteReferenceData = (item) => {
    console.log("Delete reference data:", item);
    // TODO: Implement delete functionality
  };

  useEffect(() => {
    handleRefresh();
  }, [location.pathname]);

  const handleNavigateToMetric = (item) => {
    if (!item || !item.attributes) {
      console.error("Invalid metric data:", item);
      return;
    }

    // For reference data, navigate to the metrics route structure
    if (pageConfig.isReferenceData) {
      // Get the main category from the URL (e.g., "reference-data")
      const pathSegments = location.pathname.split("/").filter(Boolean);
      const orgId = pathSegments[0];
      const mainCategory = pathSegments[1]; // e.g., "reference-data"

      const metricId =
        item.attributes.Name?.replace(/[\s_]+/g, "-")
          .replace(/-+/g, "-")
          .trim()
          .toLowerCase() || "unknown";

      // Navigate directly to the metric under the main category
      const targetUrl = `/${orgId}/metrics/${mainCategory}/${metricId}`;
      console.log(`Reference data navigating to: ${targetUrl}`);

      // Navigate to the metric detail page
      navigate(targetUrl);
      return;
    }

    // For all other metrics, use the category from the URL path
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const categoryId = pathSegments[1]; // e.g., "ghg-emissions", "energy", etc.
    const metricId =
      item.attributes.Name?.replace(/[\s_]+/g, "-")
        .replace(/-+/g, "-")
        .trim()
        .toLowerCase() || "unknown";

    console.log(`Navigating to: /${_orgId}/metrics/${categoryId}/${metricId}`);

    // Navigate to the metric detail page to view filled values and submit new forms
    navigate(`/${_orgId}/metrics/${categoryId}/${metricId}`);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return faSort;
    return sortConfig.direction === "asc" ? faSortUp : faSortDown;
  };

  const getStatusColor = (currentCount, requiredCount) => {
    if (currentCount >= requiredCount) return "success";
    if (currentCount > 0) return "warning";
    return "danger";
  };

  const getStatusText = (currentCount, requiredCount) => {
    if (currentCount >= requiredCount) return "Completed";
    if (currentCount > 0) return "In Progress";
    return "Not Started";
  };

  const getRelevantCategories = (config, data) => {
    if (!config || !data || data.length === 0) return [];

    // For Scope 3, show Upstream/Downstream categories
    if (config.filterKey === "Scope 3") {
      const categories = new Set();
      data.forEach((item) => {
        const className = item?.attributes?.ClassName || "";
        if (className.includes("Upstream")) {
          categories.add("Upstream");
        } else if (className.includes("Downstream")) {
          categories.add("Downstream");
        }
      });
      return Array.from(categories);
    }

    // For other pages, show unique categories from the data
    const categories = new Set();
    data.forEach((item) => {
      const category = item?.attributes?.Category;
      if (category) {
        categories.add(category);
      }
    });
    return Array.from(categories);
  };

  // Card view rendering function
  
  console.log("filter data->",filteredData)

  const renderCardsView = () => {
    try {
      // Special rendering for Scope 3 to show subcategories better
      if (pageConfig.filterKey === "Scope 3") {
        // Use filtered data for current selection
        const upstreamMetrics = filteredData.filter((item) =>
          item?.attributes?.ClassName?.includes("Upstream")
        );
        const downstreamMetrics = filteredData.filter((item) =>
          item?.attributes?.ClassName?.includes("Downstream")
        );

        // Group upstream metrics by specific subcategories
        const upstreamGroups = {};
        upstreamMetrics.forEach((item) => {
          const className = item?.attributes?.ClassName || "";
          let subcategory = "General Upstream";
          if (className.includes("Upstream Leased Assets"))
            subcategory = "Leased Assets";
          else if (className.includes("Upstream Transportation"))
            subcategory = "Transportation";
          else if (className.includes("Upstream Business Travel"))
            subcategory = "Business Travel";
          else if (className.includes("Upstream Employee Commuting"))
            subcategory = "Employee Commuting";
          else if (className.includes("Upstream"))
            subcategory = "General Upstream";

          if (!upstreamGroups[subcategory]) upstreamGroups[subcategory] = [];
          upstreamGroups[subcategory].push(item);
        });

        // Group downstream metrics by specific subcategories
        const downstreamGroups = {};
        downstreamMetrics.forEach((item) => {
          const className = item?.attributes?.ClassName || "";
          let subcategory = "General Downstream";
          if (className.includes("Downstream Leased Assets"))
            subcategory = "Leased Assets";
          else if (className.includes("Downstream Franchises"))
            subcategory = "Franchises";
          else if (className.includes("Downstream Investments"))
            subcategory = "Investments";
          else if (className.includes("Downstream Processing"))
            subcategory = "Processing";
          else if (className.includes("Downstream Purchased Goods"))
            subcategory = "Purchased Goods";
          else if (className.includes("Downstream Use of Sold Products"))
            subcategory = "Use of Sold Products";
          else if (className.includes("Downstream"))
            subcategory = "General Downstream";

          if (!downstreamGroups[subcategory])
            downstreamGroups[subcategory] = [];
          downstreamGroups[subcategory].push(item);
        });

        return (
          <div className="space-y-8">
            {/* Upstream Section */}
            {Object.keys(upstreamGroups).length > 0 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-2 h-10 bg-emerald-500 rounded-full"></div>
                  <h2 className="text-2xl font-bold text-emerald-700">
                    Upstream Emissions
                  </h2>
                  <Chip
                    size="lg"
                    color="primary"
                    variant="flat"
                    className="bg-emerald-100 text-emerald-700 text-sm font-semibold"
                  >
                    {upstreamMetrics.length} total metrics
                  </Chip>
                </div>

                {/* Render each upstream subcategory group */}
                {Object.entries(upstreamGroups).map(([subcategory, items]) => (
                  <div key={subcategory} className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-1 h-6 bg-emerald-400 rounded-full"></div>
                      <h3 className="text-lg font-semibold text-emerald-600">
                        {subcategory}
                      </h3>
                      <Chip
                        size="sm"
                        color="primary"
                        variant="flat"
                        className="bg-emerald-50 text-emerald-600"
                      >
                        {items.length} metrics
                      </Chip>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {items.map((item, index) => {
                        const currentCount =
                          item?.attributes?.currentCounter || 0;
                        const requiredCount =
                          item?.attributes?.requiredCounter || 1;

                        return (
                          <Card
                            key={item.id}
                            className="border border-emerald-200 hover:border-emerald-300 transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer bg-white"
                            isPressable
                            onPress={() => handleNavigateToMetric(item)}
                          >
                            <CardBody className="p-4">
                              <div className="flex items-start justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <span className="bg-emerald-100 text-emerald-700 text-xs font-semibold px-2 py-1 rounded-full">
                                    #{index + 1}
                                  </span>
                                  <Chip
                                    size="sm"
                                    variant="flat"
                                    color="primary"
                                    className="text-xs font-medium bg-emerald-100 text-emerald-700 border-emerald-200"
                                  >
                                    Upstream
                                  </Chip>
                                </div>
                              </div>

                              {/* Subcategory Badge */}
                              <div className="mb-3">
                                <Chip
                                  size="sm"
                                  variant="flat"
                                  color="default"
                                  className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200"
                                >
                                  {subcategory}
                                </Chip>
                              </div>

                              <h3 className="font-semibold text-slate-900 text-sm mb-2 line-clamp-2">
                                {item?.attributes?.Name || "Unnamed Metric"}
                              </h3>

                              <p className="text-xs text-slate-600 mb-3 line-clamp-3">
                                {item?.attributes?.Description ||
                                  "No description available"}
                              </p>

                              <div className="space-y-2">
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-slate-500">
                                    Status:
                                  </span>
                                  <Chip
                                    size="sm"
                                    color={getStatusColor(
                                      currentCount,
                                      requiredCount
                                    )}
                                    variant="flat"
                                    className="text-xs"
                                  >
                                    {getStatusText(currentCount, requiredCount)}
                                  </Chip>
                                </div>

                                {!pageConfig.isReferenceData && (
                                  <>
                                    <div className="flex items-center justify-between text-xs">
                                      <span className="text-slate-500">
                                        Recommended Frequency:
                                      </span>
                                      <span className="font-medium text-slate-700">
                                        {item?.attributes?.Frequency ||
                                          "Not set"}
                                      </span>
                                    </div>

                                    <div className="flex items-center justify-between text-xs">
                                      <span className="text-slate-500">
                                        Progress:
                                      </span>
                                      <span className="font-medium text-slate-700">
                                        {currentCount}/{requiredCount}
                                      </span>
                                    </div>
                                  </>
                                )}

                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-slate-500">
                                    Category:
                                  </span>
                                  <span className="font-medium text-slate-700">
                                    {item?.attributes?.Category ||
                                      "Uncategorized"}
                                  </span>
                                </div>
                              </div>
                            </CardBody>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Downstream Section */}
            {Object.keys(downstreamGroups).length > 0 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-2 h-10 bg-purple-500 rounded-full"></div>
                  <h2 className="text-2xl font-bold text-purple-700">
                    Downstream Emissions
                  </h2>
                  <Chip
                    size="lg"
                    color="secondary"
                    variant="flat"
                    className="bg-purple-100 text-purple-700 text-sm font-semibold"
                  >
                    {downstreamMetrics.length} total metrics
                  </Chip>
                </div>

                {/* Render each downstream subcategory group */}
                {Object.entries(downstreamGroups).map(
                  ([subcategory, items]) => (
                    <div key={subcategory} className="space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-1 h-6 bg-purple-400 rounded-full"></div>
                        <h3 className="text-lg font-semibold text-purple-600">
                          {subcategory}
                        </h3>
                        <Chip
                          size="sm"
                          color="secondary"
                          variant="flat"
                          className="bg-purple-50 text-purple-600"
                        >
                          {items.length} metrics
                        </Chip>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {items
                          .map((item, index) => {
                            const currentCount =
                              item?.attributes?.currentCounter || 0;
                            const requiredCount =
                              item?.attributes?.requiredCounter || 1;

                            return (
                              <Card
                                key={item.id}
                                className="border border-purple-200 hover:border-purple-300 transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer bg-white"
                                isPressable
                                onPress={() => handleNavigateToMetric(item)}
                              >
                                <CardBody className="p-4">
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                      <span className="bg-purple-100 text-purple-700 text-xs font-semibold px-2 py-1 rounded-full">
                                        #{index + 1}
                                      </span>
                                      <Chip
                                        size="sm"
                                        variant="flat"
                                        color="secondary"
                                        className="text-xs font-medium bg-purple-100 text-purple-700 border-purple-200"
                                      >
                                        Downstream
                                      </Chip>
                                    </div>
                                  </div>

                                  {/* Subcategory Badge */}
                                  <div className="mb-3">
                                    <Chip
                                      size="sm"
                                      variant="flat"
                                      color="default"
                                      className="text-xs bg-purple-50 text-purple-700 border-purple-200"
                                    >
                                      {subcategory}
                                    </Chip>
                                  </div>

                                  <h3 className="font-semibold text-slate-900 text-sm mb-2 line-clamp-2">
                                    {item?.attributes?.Name || "Unnamed Metric"}
                                  </h3>

                                  <p className="text-xs text-slate-600 mb-3 line-clamp-3">
                                    {item?.attributes?.Description ||
                                      "No description available"}
                                  </p>

                                  <div className="space-y-2">
                                    <div className="flex items-center justify-between text-xs">
                                      <span className="text-slate-500">
                                        Status:
                                      </span>
                                      <Chip
                                        size="sm"
                                        color={getStatusColor(
                                          currentCount,
                                          requiredCount
                                        )}
                                        variant="flat"
                                        className="text-xs"
                                      >
                                        {getStatusText(
                                          currentCount,
                                          requiredCount
                                        )}
                                      </Chip>
                                    </div>

                                    {!pageConfig.isReferenceData && (
                                      <>
                                        <div className="flex items-center justify-between text-xs">
                                          <span className="text-slate-500">
                                            Recommended Frequency:
                                          </span>
                                          <span className="font-medium text-slate-700">
                                            {item?.attributes?.Frequency ||
                                              "Not set"}
                                          </span>
                                        </div>

                                        <div className="flex items-center justify-between text-xs">
                                          <span className="text-slate-500">
                                            Progress:
                                          </span>
                                          <span className="font-medium text-slate-700">
                                            {currentCount}/{requiredCount}
                                          </span>
                                        </div>
                                      </>
                                    )}

                                    <div className="flex items-center justify-between text-xs">
                                      <span className="text-slate-500">
                                        Category:
                                      </span>
                                      <span className="font-medium text-slate-700">
                                        {item?.attributes?.Category ||
                                          "Uncategorized"}
                                      </span>
                                    </div>
                                  </div>
                                </CardBody>
                              </Card>
                            );
                          })
                          .filter(Boolean)}
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        );
      }

      // Regular rendering for other pages
      const startIndex = (currentPage - 1) * 10;
      const currentPageData = filteredData.slice(startIndex, startIndex + 10);
      console.log("filter data", filteredData);
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {currentPageData.map((item, index) => {
            // Safety check for Symbol values in cards view
            try {
              if (item && item.attributes) {
                const hasSymbolValues = Object.values(item.attributes).some(
                  (value) =>
                    typeof value === "symbol" ||
                    (Array.isArray(value) &&
                      value.some((v) => typeof v === "symbol")) ||
                    (value &&
                      typeof value === "object" &&
                      Object.values(value).some((v) => typeof v === "symbol"))
                );

                if (hasSymbolValues) {
                  console.warn(
                    "Symbol values detected in cards view item, skipping:",
                    item
                  );
                  return null;
                }
              }
            } catch (err) {
              console.warn(
                "Error checking for Symbol values in cards view:",
                err
              );
              return null;
            }

            const currentCount = item?.attributes?.currentCounter || 0;
            const requiredCount = item?.attributes?.requiredCounter || 1;

            return (
              <Card
                key={item.id}
                className="border border-slate-200 hover:border-emerald-300 transition-all duration-300 shadow-sm hover:shadow-lg cursor-pointer bg-gradient-to-br from-white to-slate-50"
                isPressable
                onPress={() => handleNavigateToMetric(item)}
              >
                <CardBody className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="bg-emerald-100 text-emerald-700 text-xs font-semibold px-2 py-1 rounded-full">
                        #{startIndex + index + 1}
                      </span>
                      <Chip
                        size="sm"
                        color={getStatusColor(currentCount, requiredCount)}
                        variant="flat"
                        className="text-xs"
                      >
                        {getStatusText(currentCount, requiredCount)}
                      </Chip>
                    </div>
                  </div>

                  <h3 className="font-semibold text-slate-900 text-sm mb-2 line-clamp-2">
                    {item?.attributes?.Name || "Unnamed Metric"}
                  </h3>

                  <p className="text-xs text-slate-600 mb-3 line-clamp-3">
                    {item?.attributes?.Description ||
                      "No description available"}
                  </p>

                  <div className="space-y-2">
                    {!pageConfig.isReferenceData && (
                      <>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-500">
                            Recommended Frequency:
                          </span>
                          <span className="font-medium text-slate-700">
                            {item?.attributes?.Frequency || "Not set"}
                          </span>
                        </div>

                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-500">Progress:</span>
                          <span className="font-medium text-slate-700">
                            {currentCount}/{requiredCount}
                          </span>
                        </div>
                      </>
                    )}

                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">Category:</span>
                      <span className="font-medium text-slate-700">
                        {item?.attributes?.Category || "Uncategorized"}
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            );
          })}
        </div>
      );
    } catch (error) {
      console.error("Error rendering cards view:", error);
      return (
        <div className="p-4 text-center text-red-600">
          Error rendering cards view. Please refresh the page.
        </div>
      );
    }
  };

  // List view rendering function
  const renderListView = () => {
    try {
      if (!filteredData || filteredData.length === 0) {
        return (
          <div className="p-6 text-center text-neutral-500">
            <div className="text-lg font-semibold mb-2">No Data Available</div>
            <div className="text-sm">
              No data found matching your current filters.
            </div>
          </div>
        );
      }

      return (
        <div className="space-y-4">
          {Object.keys(groupedData)
            .map((className, groupIndex) => {
              try {
                const groupMetrics = groupedData[className];
                if (!Array.isArray(groupMetrics)) {
                  console.warn(
                    "Invalid groupMetrics for className:",
                    className,
                    groupMetrics
                  );
                  return null;
                }

                const completedCount = groupMetrics.filter(
                  (item) =>
                    (item?.attributes?.currentCounter || 0) >=
                    (item?.attributes?.requiredCounter || 1)
                ).length;
                const totalCount = groupMetrics.length;

                // Special styling for Scope 3 subcategories
                const isUpstream =
                  pageConfig.filterKey === "Scope 3" &&
                  className.includes("Upstream");
                const isDownstream =
                  pageConfig.filterKey === "Scope 3" &&
                  className.includes("Downstream");

                const cardBorderClass = isUpstream
                  ? "border-emerald-200"
                  : isDownstream
                  ? "border-purple-200"
                  : "border-neutral-200";
                const headerBgClass = isUpstream
                  ? "from-emerald-50 to-white"
                  : isDownstream
                  ? "from-purple-50 to-white"
                  : "from-neutral-50 to-white";
                const textColorClass = isUpstream
                  ? "text-emerald-900"
                  : isDownstream
                  ? "text-purple-900"
                  : "text-neutral-900";

                return (
                  <Card
                    key={`list-group-${groupIndex}`}
                    className={`border ${cardBorderClass} shadow-sm`}
                  >
                    <CardBody className="p-0">
                      <div
                        className={`px-4 py-3 border-b ${cardBorderClass} bg-gradient-to-r ${headerBgClass}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span
                              className={`font-semibold text-lg ${textColorClass}`}
                            >
                              {safeString(className, "Unknown Category")}
                            </span>
                            <Chip
                              size="sm"
                              color={
                                completedCount === totalCount
                                  ? "success"
                                  : completedCount > 0
                                  ? "warning"
                                  : "danger"
                              }
                              variant="flat"
                              className="text-xs"
                            >
                              {completedCount}/{totalCount} Complete
                            </Chip>
                          </div>
                        </div>
                      </div>

                      <div className="p-0">
                        <div className="divide-y divide-neutral-200">
                          {groupMetrics
                            .map((item, itemIndex) => {
                              // Safety checks
                              if (!item || typeof item === "symbol") {
                                console.warn(
                                  "Skipping invalid item:",
                                  item,
                                  "at index:",
                                  itemIndex
                                );
                                return null;
                              }

                              if (
                                !item.attributes ||
                                typeof item.attributes !== "object"
                              ) {
                                console.warn(
                                  "Item missing attributes:",
                                  item,
                                  "at index:",
                                  itemIndex
                                );
                                return null;
                              }

                              if (pageConfig.isReferenceData) {
                                // Reference data list items
                                try {
                                  const description =
                                    item?.attributes?.Description || "--";
                                  const type =
                                    item?.attributes?.ClassName || "--";
                                  const createdBy =
                                    item?.attributes?.createdBy?.data
                                      ?.attributes?.username || "--";

                                  return (
                                    <div
                                      key={item.id}
                                      className="px-4 py-3 hover:bg-neutral-50 transition-colors"
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-4">
                                            <div className="flex-1 min-w-0">
                                              <h4 className="font-semibold text-slate-900 text-sm mb-2 line-clamp-2">
                                                {StringLimit(
                                                  safeString(
                                                    item?.attributes?.Name,
                                                    ""
                                                  ),
                                                  50
                                                ) || "--"}
                                              </h4>
                                              <p className="text-xs text-slate-500">
                                                {StringLimit(
                                                  safeString(description, ""),
                                                  80
                                                ) || "--"}
                                              </p>
                                            </div>
                                            <div className="flex items-center gap-6 text-xs text-slate-500">
                                              <span className="min-w-[80px]">
                                                <span className="font-medium">
                                                  Type:
                                                </span>{" "}
                                                {StringLimit(
                                                  safeString(type, ""),
                                                  25
                                                ) || "--"}
                                              </span>
                                              <span className="min-w-[100px]">
                                                <span className="font-medium">
                                                  By:
                                                </span>{" "}
                                                {StringLimit(
                                                  safeString(createdBy, ""),
                                                  20
                                                ) || "--"}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2 ml-4">
                                          <Button
                                            size="sm"
                                            variant="flat"
                                            color="primary"
                                            onPress={() =>
                                              handleEditReferenceData(item)
                                            }
                                            className="text-xs font-medium"
                                          >
                                            Edit
                                          </Button>
                                          <Button
                                            size="sm"
                                            variant="flat"
                                            color="danger"
                                            onPress={() =>
                                              handleDeleteReferenceData(item)
                                            }
                                            className="text-xs font-medium"
                                          >
                                            Delete
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                } catch (rowError) {
                                  console.error(
                                    "Error rendering reference data list item:",
                                    rowError,
                                    item
                                  );
                                  return (
                                    <div
                                      key={`error-${item.id || itemIndex}`}
                                      className="px-4 py-3 bg-red-50 border-l-4 border-red-200"
                                    >
                                      <p className="text-red-600 text-sm">
                                        Error rendering item data
                                      </p>
                                    </div>
                                  );
                                }
                              } else {
                                // Regular metrics list items
                                try {
                                  const name = safeString(
                                    item?.attributes?.Name,
                                    ""
                                  );
                                  const frequency = safeString(
                                    item?.attributes?.Frequency,
                                    ""
                                  );
                                  const currentCount =
                                    item?.attributes?.currentCounter || 0;
                                  const requiredCount =
                                    item?.attributes?.requiredCounter || 1;
                                  const status = getStatusText(
                                    currentCount,
                                    requiredCount
                                  );

                                  return (
                                    <div
                                      key={item.id}
                                      className="px-4 py-3 hover:bg-neutral-50 transition-colors"
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center gap-6">
                                            <div className="flex-1 min-w-0">
                                              <h4 className="font-semibold text-slate-900 text-sm mb-2 line-clamp-2">
                                                {StringLimit(name, 50) || "--"}
                                              </h4>
                                              <p className="text-xs text-slate-500">
                                                Frequency:{" "}
                                                {StringLimit(frequency, 30) ||
                                                  "--"}
                                              </p>
                                            </div>
                                            <div className="flex items-center gap-6 text-xs text-slate-500">
                                              <div className="flex items-center gap-2">
                                                <span className="font-medium">
                                                  Status:
                                                </span>
                                                <Chip
                                                  size="sm"
                                                  color={
                                                    currentCount ===
                                                    requiredCount
                                                      ? "success"
                                                      : currentCount > 0
                                                      ? "warning"
                                                      : "danger"
                                                  }
                                                  variant="flat"
                                                  className="text-xs"
                                                >
                                                  {status}
                                                </Chip>
                                              </div>
                                              <span className="min-w-[80px]">
                                                <span className="font-medium">
                                                  Progress:
                                                </span>{" "}
                                                {currentCount}/{requiredCount}
                                              </span>
                                              <span className="min-w-[80px]">
                                                <span className="font-medium">
                                                  Required:
                                                </span>{" "}
                                                {requiredCount}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                } catch (rowError) {
                                  console.error(
                                    "Error rendering metrics list item:",
                                    rowError,
                                    item
                                  );
                                  return (
                                    <div
                                      key={`error-${item.id || itemIndex}`}
                                      className="px-4 py-3 bg-red-50 border-l-4 border-red-200"
                                    >
                                      <p className="text-red-600 text-sm">
                                        Error rendering item data
                                      </p>
                                    </div>
                                  );
                                }
                              }
                            })
                            .filter(Boolean)}
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                );
              } catch (groupError) {
                console.error(
                  "Error rendering list group:",
                  className,
                  groupError
                );
                return (
                  <Card
                    key={`error-list-${groupIndex}`}
                    className="border border-red-200 bg-red-50"
                  >
                    <CardBody className="p-4">
                      <div className="text-center text-red-600">
                        Error rendering group:{" "}
                        {safeString(className, "Unknown")}
                      </div>
                    </CardBody>
                  </Card>
                );
              }
            })
            .filter(Boolean)}
        </div>
      );
    } catch (error) {
      console.error("Error rendering list view:", error);
      return (
        <div className="p-4 text-center text-red-600">
          Error rendering list view. Please refresh the page.
        </div>
      );
    }
  };

  const handleViewAnalytics = () => {
    navigate(`/${_orgId}/sustainability-activities`);
  };

  const handleExportData = async () => {
    if (isExporting) return;

    setIsExporting(true);
    setError(null);

    try {
      // Export logic here
      console.log("Exporting data...");
    } catch (err) {
      setError("Failed to export data. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleCreateMetric = () => {
    // Navigate to the appropriate create route based on the current page context
    if (pageConfig.filterKey && pageConfig.filterKey.includes("Task Tracker")) {
      // For task tracker pages, navigate to the specific create route
      const pathSegments = location.pathname.split("/").filter(Boolean);
      const subCategory = pathSegments[2]; // e.g., 'water', 'energy', etc.
      if (subCategory === "water") {
        navigate(`/${_orgId}/task-tracker/water/create`);
      } else {
        navigate(`/${_orgId}/metrics-builder`);
      }
    } else {
      // For other pages, navigate to the general metrics builder
      navigate(`/${_orgId}/metrics-builder`);
    }
  };

  if (isLoading | isPending) {
    return <LoadingPage loadingText="Loading page data..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">{error}</p>
            <button
              onClick={handleRefresh}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Error boundary for rendering
  if (renderError) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-red-800 mb-2">
              Rendering Error
            </h2>
            <p className="text-red-700 mb-4">{renderError.message}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setRenderError(null)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Try Again
              </button>
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-slate-600 text-white rounded-md hover:bg-slate-700"
              >
                Refresh Data
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-white to-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">
                  {pageConfig.title}
                </h1>
                <p className="text-slate-600 mt-1">{pageConfig.description}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Breadcrumbs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-4 py-2">
        <BreadCrumbsPage />
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg border shadow-sm p-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex-1">
              <SearchInput
                placeholderText="Search metrics by name, description, or category..."
                searchText={searchTerm}
                setSearchText={handleSearch}
              />
            </div>

            <div className="flex items-center gap-3">
              {/* Layout Toggle */}
              <div className="flex bg-slate-100 rounded-lg p-1">
                <Button
                  size="sm"
                  variant={layoutMode === "cards" ? "solid" : "light"}
                  color={layoutMode === "cards" ? "success" : "default"}
                  onClick={() => {
                    // Prevent rapid view switching that could cause error loops
                    if (layoutMode !== "cards" && !isViewSwitching) {
                      setIsViewSwitching(true);
                      setLayoutMode("cards");
                      // Reset view switching state after a short delay
                      setTimeout(() => setIsViewSwitching(false), 500);
                    }
                  }}
                  disabled={isViewSwitching}
                  className="px-3 py-1 text-xs"
                >
                  <FontAwesomeIcon
                    icon={faGripVertical}
                    className="w-3 h-3 mr-1"
                  />
                  Cards
                </Button>
                <Button
                  size="sm"
                  variant={layoutMode === "list" ? "solid" : "light"}
                  color={layoutMode === "list" ? "success" : "default"}
                  onClick={() => {
                    // Prevent rapid view switching that could cause error loops
                    if (layoutMode !== "list" && !isViewSwitching) {
                      setIsViewSwitching(true);
                      setLayoutMode("list");
                      // Reset view switching state after a short delay
                      setTimeout(() => setIsViewSwitching(false), 500);
                    }
                  }}
                  disabled={isViewSwitching}
                  className="px-3 py-1 text-xs"
                >
                  <FontAwesomeIcon icon={faList} className="w-3 h-3 mr-1" />
                  List
                </Button>
              </div>

              {/* Refresh Button */}
              <Button
                variant="flat"
                startContent={<FontAwesomeIcon icon={faRefresh} />}
                onClick={handleRefresh}
                className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
              >
                Refresh
              </Button>
            </div>
          </div>
        </div>

        {/* Content Display */}
        {isViewSwitching ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 text-center">
            <div className="text-emerald-600 text-lg font-semibold mb-2">
              Switching View
            </div>
            <div className="text-emerald-700 mb-4">
              Please wait while we prepare the{" "}
              {layoutMode === "cards" ? "cards" : "list"} view...
            </div>
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
            </div>
          </div>
        ) : renderError ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <div className="text-red-600 text-lg font-semibold mb-2">
              Rendering Error
            </div>
            <div className="text-red-700 mb-4">
              {renderError.message ===
              "Data contains invalid values. Refreshing automatically..."
                ? "We detected some data issues and are automatically refreshing the page to resolve them. This should only take a moment."
                : renderError.message ||
                  "An error occurred while rendering the content"}
            </div>
            <div className="flex gap-2 justify-center">
              <Button
                size="sm"
                color="danger"
                variant="flat"
                onClick={() => {
                  setRenderError(null);
                  setIsAutoRefreshing(false);
                  console.log("User manually cleared render error");
                }}
                disabled={isAutoRefreshing}
              >
                Try Again
              </Button>
              <Button
                size="sm"
                variant="flat"
                onClick={() => {
                  setRenderError(null);
                  setIsAutoRefreshing(false);
                  handleRefresh();
                  console.log("User manually triggered refresh");
                }}
                className="bg-slate-700 text-white"
                disabled={isAutoRefreshing}
              >
                Refresh Data
              </Button>
            </div>
            {isAutoRefreshing && (
              <div className="mt-3 text-sm text-red-600 flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                ⏱️ Auto-refresh in progress...
              </div>
            )}
          </div>
        ) : filteredData.length === 0 ? (
          <NullData
            title={`No ${pageConfig.filterKey.toLowerCase()} metrics found`}
            description={`Click on "Create new metric" to add one.`}
          />
        ) : (
          <>
            {layoutMode === "cards" ? renderCardsView() : renderListView()}

            {/* Pagination for non-Scope 3 pages */}
            {pageConfig.filterKey !== "Scope 3" && filteredData.length > 10 && (
              <div className="flex justify-center mt-8">
                <Pagination
                  total={Math.ceil(filteredData.length / 10)}
                  page={currentPage}
                  onChange={setCurrentPage}
                  showControls
                  color="success"
                  className="gap-2"
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PlaceholderPage;
